import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Bio } from "../../data/constanit";
import { Menu, X, Sun, Moon, Download } from "lucide-react";
import { FaGithub as Github } from "react-icons/fa";
import MagneticButton from "../common/MagneticButton";

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 0 24px;
  transition: background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease;

  ${({ scrolled, theme }) =>
    scrolled
      ? `
    background: ${theme.bgGlass || "rgba(3, 7, 18, 0.85)"};
    backdrop-filter: blur(14px);
    border-bottom: 1px solid ${theme.border};
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  `
      : `
    background: transparent;
    border-bottom: 1px solid transparent;
  `}
`;

const NavContainer = styled.div`
  width: 100%;
  max-width: 1280px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BrandLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  cursor: pointer;
`;

const LogoGlyph = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%);
  display: grid;
  place-items: center;
  color: #030712;
  font-weight: 900;
  font-size: 15px;
  font-family: monospace;
  box-shadow: 0 0 16px rgba(6, 182, 212, 0.4);
`;

const BrandText = styled.span`
  span {
    color: #06b6d4;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: 860px) {
    display: none;
  }
`;

const NavItem = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14.5px;
  font-weight: 600;
  position: relative;
  transition: color 0.2s ease;

  &:hover {
    color: #06b6d4;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 0%;
    height: 2px;
    background: #06b6d4;
    transition: width 0.2s ease;
    border-radius: 999px;
  }

  &:hover::after {
    width: 100%;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #06b6d4;
    color: #06b6d4;
    transform: translateY(-2px);
  }
`;

const GithubCta = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
  border: 1px solid rgba(6, 182, 212, 0.4);
  color: ${({ theme }) => theme.text_primary};
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(135deg, #06b6d4 0%, #0284c7 100%);
    color: #030712;
    border-color: #06b6d4;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(6, 182, 212, 0.4);
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  place-items: center;
  cursor: pointer;

  @media (max-width: 860px) {
    display: grid;
  }
`;

const MobileDrawer = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.bgGlass || "rgba(3, 7, 18, 0.95)"};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 9998;
`;

const MobileNavLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 16px;
  font-weight: 600;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    color: #06b6d4;
  }
`;

const Navbar = ({ darkMode, setDarkMode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "About", href: "#about" },
    { label: "Skills", href: "#skills" },
    { label: "DSA", href: "#dsa" },
    { label: "Experience", href: "#experience" },
    { label: "Projects", href: "#projects" },
    { label: "Education", href: "#education" },
    { label: "Contact", href: "#contact" }
  ];

  return (
    <>
      <Nav scrolled={scrolled}>
        <NavContainer>
          <BrandLink href="#about">
            <LogoGlyph>VC</LogoGlyph>
            <BrandText>
              Vivek<span>.dev</span>
            </BrandText>
          </BrandLink>

          <NavLinks>
            {navItems.map((item) => (
              <NavItem key={item.label} href={item.href}>
                {item.label}
              </NavItem>
            ))}
          </NavLinks>

          <ActionGroup>
            <IconButton
              onClick={() => setDarkMode(!darkMode)}
              aria-label="Toggle theme mode"
              title={darkMode ? "Switch to light theme" : "Switch to dark theme"}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>

            <MagneticButton>
              <GithubCta
                href={Bio.github}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github size={16} />
                <span>GitHub</span>
              </GithubCta>
            </MagneticButton>

            <MobileMenuBtn
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </MobileMenuBtn>
          </ActionGroup>
        </NavContainer>
      </Nav>

      <AnimatePresence>
        {mobileOpen && (
          <MobileDrawer
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
          >
            {navItems.map((item) => (
              <MobileNavLink
                key={item.label}
                href={item.href}
                onClick={() => setMobileOpen(false)}
              >
                <span>{item.label}</span>
                <span style={{ color: "#06b6d4" }}>&rarr;</span>
              </MobileNavLink>
            ))}

            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <a
                href={Bio.github}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "12px",
                  borderRadius: "10px",
                  background: "#06b6d4",
                  color: "#030712",
                  fontWeight: 700,
                  textDecoration: "none"
                }}
              >
                <Github size={16} /> GitHub Profile
              </a>
              <a
                href={Bio.resume}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "12px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.08)",
                  color: "#f8fafc",
                  fontWeight: 700,
                  textDecoration: "none",
                  border: "1px solid rgba(255, 255, 255, 0.15)"
                }}
              >
                <Download size={16} /> Resume
              </a>
            </div>
          </MobileDrawer>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
